import { apiClient } from '../../../../utils/apiClient';

/**
 * Service for handling audit log API operations
 * Provides abstraction layer for all audit log related API calls
 */
export class AuditLogService {
  /**
   * Fetch audit logs with filters and pagination
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (1-based)
   * @param {number} options.limit - Number of items per page
   * @param {string} options.actionType - Filter by action type
   * @param {string} options.userId - Filter by user ID
   * @param {string} options.startDate - Filter by start date (YYYY-MM-DD)
   * @param {string} options.endDate - Filter by end date (YYYY-MM-DD)
   * @returns {Promise<Object>} API response with audit logs and pagination
   */
  static async fetchAuditLogs(options = {}) {
    const {
      page = 1,
      limit = 20,
      actionType = '',
      userId = '',
      startDate = '',
      endDate = ''
    } = options;

    // Build query parameters
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    // Add filters if they exist
    if (actionType) params.append('actionType', actionType);
    if (userId) params.append('userId', userId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    try {
      const response = await apiClient.get(`/audit-logs?${params.toString()}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch audit logs'
      };
    }
  }

  /**
   * Export audit logs as CSV with current filters
   * @param {Object} filters - Current filter state
   * @param {string} filters.actionType - Filter by action type
   * @param {string} filters.userId - Filter by user ID
   * @param {string} filters.startDate - Filter by start date
   * @param {string} filters.endDate - Filter by end date
   * @returns {Promise<Object>} Success/error response
   */
  static async exportAuditLogsCSV(filters = {}) {
    const { actionType, userId, startDate, endDate } = filters;

    // Build query parameters based on current filters
    const params = new URLSearchParams();
    if (actionType) params.append('actionType', actionType);
    if (userId) params.append('userId', userId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    try {
      // Make request to export endpoint with same filters
      const response = await apiClient.get(`/audit-logs/export?${params.toString()}`, {
        responseType: 'blob' // Important for handling file download
      });

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'audit_logs.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to export audit logs'
      };
    }
  }

  /**
   * Format date and time for display
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date and time
   */
  static formatDateTime(dateString) {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * Get badge color class for action types
   * @param {string} action - Action type
   * @returns {string} Tailwind CSS classes for badge styling
   */
  static getActionBadgeColor(action) {
    switch (action) {
      case 'CLAIM_CREATED':
        return 'bg-blue-100 text-blue-800';
      case 'CLAIM_APPROVED':
        return 'bg-green-100 text-green-800';
      case 'CLAIM_REJECTED':
        return 'bg-red-100 text-red-800';
      case 'STATUS_CHANGE':
        return 'bg-yellow-100 text-yellow-800';
      case 'DOCUMENT_UPLOAD':
        return 'bg-purple-100 text-purple-800';
      case 'SLA_BREACH':
        return 'bg-red-100 text-red-800';
      case 'SLA_WARNING':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Available action types for filtering
   * @returns {Array<Object>} Array of action type options
   */
  static getActionTypeOptions() {
    return [
      { value: '', label: 'All Actions' },
      { value: 'CLAIM_CREATED', label: 'Claim Created' },
      { value: 'CLAIM_UPDATED', label: 'Claim Updated' },
      { value: 'CLAIM_APPROVED', label: 'Claim Approved' },
      { value: 'CLAIM_REJECTED', label: 'Claim Rejected' },
      { value: 'STATUS_CHANGE', label: 'Status Change' },
      { value: 'DOCUMENT_UPLOAD', label: 'Document Upload' },
      { value: 'SLA_STARTED', label: 'SLA Started' },
      { value: 'SLA_WARNING', label: 'SLA Warning' },
      { value: 'SLA_BREACH', label: 'SLA Breach' }
    ];
  }
}

export default AuditLogService;